import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { generacionApi, type ContenidoItem } from '../lib/api';
import { Toast, type ToastState } from '../components/Toast';

// ─── Preview types ─────────────────────────────────────────────────────────
interface SlideShape { titulo: string; cuerpo: string | null; cta: string | null }
interface CuerpoTexto    { caption: string; hashtags: string[] }
interface CuerpoCarrusel { slides: SlideShape[] }
interface CuerpoHistoria { texto: string; poll: { pregunta: string; opcion_a: string; opcion_b: string } }
interface CuerpoReel     { gancho: string; desarrollo: string; cta: string; duracion_estimada: string }
interface CuerpoPdf      { intro: string; secciones: Array<{ titulo: string; contenido: string }>; conclusion: string }

// 'texto' es exclusivo de la forma legacy — distingue de NuevoHistoriaPoll (que también tiene 'poll' pero usa 'texto_principal')
function isTexto(x: unknown): x is CuerpoTexto    { return typeof x === 'object' && x !== null && 'caption' in x; }
function isCarrusel(x: unknown): x is CuerpoCarrusel { return typeof x === 'object' && x !== null && 'slides' in x; }
function isHistoria(x: unknown): x is CuerpoHistoria { return typeof x === 'object' && x !== null && 'texto' in x && 'poll' in x; }
function isReel(x: unknown): x is CuerpoReel      { return typeof x === 'object' && x !== null && 'gancho' in x; }
function isPdf(x: unknown): x is CuerpoPdf        { return typeof x === 'object' && x !== null && 'secciones' in x; }

interface NuevoPost     { estructura_visual: Record<string, string>; descripcion: Record<string, unknown>; idea_diseno: string }
interface NuevoCarrusel { slides: Array<{ numero: number; total: number; formula?: string; titulo: string; cuerpo: string; idea_diseno: string }>; descripcion: Record<string, unknown> }
interface NuevoPdf      { portada: Record<string, string>; seccion_psicoeducativa: Record<string, string>; ejercicio: Record<string, unknown>; consejo_final: Record<string, string>; material_sugerido?: { titulo: string; tipo: string; descripcion: string; contenido_sugerido: string[] }; cta: Record<string, string> }
interface NuevoReel     { formato_elegido: string; razon_formato: string; guion: Record<string, unknown>; ideas_edicion: string[]; musica_sugerida: string; descripcion: Record<string, unknown> }
interface NuevoHistoriaCta { frase_valor: string; instruccion: string; palabra_clave_destacada: string; texto_inferior: string; idea_diseno: string }
interface NuevoHistoriaPoll { texto_principal: string; poll: { pregunta: string; opcion_1: string; opcion_2: string }; texto_inferior: string; idea_diseno: string }

// Reel v2 — guion enriquecido con fórmulas de viralidad
interface ReelSegmento { se_dice: string; texto_pantalla: string; instruccion_visual: string }
interface ReelGanchoSeg extends ReelSegmento { tiempo: string; formula: string }
interface ReelLoopSeg extends ReelSegmento { tiempo: string }
interface ReelBloqueSegmentos { tiempo: string; segmentos: ReelSegmento[] }
interface ReelCtaSeg extends ReelSegmento { tiempo: string; variante: string }
interface ReelCierreLoop { tiempo: string; se_dice: string | null }
interface ReelGuionV2 {
  gancho: ReelGanchoSeg;
  loop_promesa: ReelLoopSeg;
  desarrollo: ReelBloqueSegmentos;
  solucion: ReelBloqueSegmentos;
  cta: ReelCtaSeg;
  cierre_loop: ReelCierreLoop;
}
interface ReelEdicionV2 { ritmo_gancho_desarrollo: string; ritmo_parte_emocional: string; ideas_edicion: string[]; musica_sugerida: string }
interface ReelValidacionDuracion { palabras_totales_habladas: number; duracion_estimada_segundos: number }
interface ReelV2 {
  formato_elegido: string;
  razon_formato: string;
  guion: ReelGuionV2;
  edicion: ReelEdicionV2;
  descripcion: Record<string, unknown>;
  validacion_duracion: ReelValidacionDuracion;
}

function isNuevoPost(x: unknown): x is NuevoPost       { return typeof x === 'object' && x !== null && 'estructura_visual' in x; }
function isNuevoCarrusel(x: unknown): x is NuevoCarrusel { return typeof x === 'object' && x !== null && 'slides' in x && 'descripcion' in x; }
function isNuevoPdf(x: unknown): x is NuevoPdf         { return typeof x === 'object' && x !== null && 'portada' in x; }
function isReelV2(x: unknown): x is ReelV2              { return typeof x === 'object' && x !== null && 'guion' in x && 'edicion' in x; }
function isNuevoReel(x: unknown): x is NuevoReel        { return typeof x === 'object' && x !== null && 'guion' in x; }
function isNuevoHistoriaCta(x: unknown): x is NuevoHistoriaCta { return typeof x === 'object' && x !== null && 'frase_valor' in x; }
function isNuevoHistoriaPoll(x: unknown): x is NuevoHistoriaPoll { return typeof x === 'object' && x !== null && 'texto_principal' in x; }

function FieldMissing({ label }: { label: string }) {
  return <span className="field-missing">⚠ {label} faltante</span>;
}

const REEL_FORMULA_LABEL: Record<string, string> = {
  negacion_expectativa: 'Negación de expectativa',
  confesion: 'Confesión / vulnerabilidad',
  pregunta_duele: 'Pregunta que duele',
  dato_sorprende: 'Dato que sorprende',
  verdad_incomoda: 'Verdad incómoda',
};

const REEL_VARIANTE_LABEL: Record<string, string> = {
  directo: 'Directo',
  urgencia: 'Urgencia',
  pregunta: 'Pregunta',
};

// ─── Snippet extractor ────────────────────────────────────────────────────
function getSnippet(cuerpo: unknown): string {
  if (typeof cuerpo === 'string') return cuerpo.slice(0, 80);
  if (typeof cuerpo !== 'object' || cuerpo === null) return '';
  const obj = cuerpo as Record<string, unknown>;
  if (obj.portada) { const p = obj.portada as Record<string, string>; return String(p.titulo_emocional ?? '').slice(0, 80); }
  if (obj.estructura_visual) { const ev = obj.estructura_visual as Record<string, string>; return String(ev.linea_1 ?? '').slice(0, 80); }
  if (obj.guion) { const g = obj.guion as Record<string, Record<string, string>>; return String(g.gancho?.texto ?? '').slice(0, 80); }
  if (obj.frase_valor) return String(obj.frase_valor).slice(0, 80);
  const main = (obj.caption ?? obj.gancho ?? obj.texto ?? obj.intro) as string | undefined;
  if (main) return String(main).slice(0, 80);
  if (Array.isArray(obj.slides) && obj.slides.length > 0) { const s = obj.slides[0] as Record<string, unknown>; return String(s.titulo ?? '').slice(0, 80); }
  return '';
}

// ─── Preview cards ────────────────────────────────────────────────────────
const TIPO_COLOR: Record<string, string> = {
  texto: '#C49A8A', carrusel: '#B8D4C8', reel: '#A89080',
  historia: '#B8B4D4', historia_cta: '#C4B8D4', pdf: '#A8D4C0',
};

function PreviewCard({ label, children, color }: { label: string; children: React.ReactNode; color: string }) {
  return (
    <div className="preview-card" style={{ borderLeftColor: color } as React.CSSProperties}>
      <div className="preview-card-label">{label}</div>
      {children}
    </div>
  );
}

function Preview({ item }: { item: ContenidoItem }) {
  const c = item.cuerpo;
  const color = TIPO_COLOR[item.tipo] ?? '#C49A8A';

  if (item.tipo === 'texto' && isTexto(c)) return (
    <div className="preview-grid">
      <PreviewCard label="Caption de Instagram" color={color}>
        <div className="preview-card-main">{c.caption}</div>
        {c.hashtags.length > 0 && <div className="preview-hashtags">{c.hashtags.map(tag => <span key={tag} className="preview-hashtag">#{tag}</span>)}</div>}
      </PreviewCard>
    </div>
  );

  if (item.tipo === 'carrusel' && isCarrusel(c)) return (
    <div className="preview-grid">
      <div className="preview-card-label" style={{ paddingLeft: 0 }}>Carrusel — {c.slides.length} slides</div>
      <div className="slides-grid">
        {c.slides.map((slide, i) => (
          <div key={i} className="slide-card">
            <div className="slide-num">Slide {i + 1}</div>
            <div className="slide-t">{slide.titulo}</div>
            {slide.cuerpo && <div className="slide-b">{slide.cuerpo}</div>}
            {slide.cta && <div className="slide-cta">{slide.cta}</div>}
          </div>
        ))}
      </div>
    </div>
  );

  if (item.tipo === 'historia' && isNuevoHistoriaPoll(c)) return (
    <div className="preview-grid">
      <PreviewCard label="Historia de Instagram" color={color}>
        <div className="preview-card-main">{c.texto_principal || <FieldMissing label="texto_principal" />}</div>
      </PreviewCard>
      <div className="poll-card">
        <div className="poll-q">📊 {c.poll?.pregunta || <FieldMissing label="poll.pregunta" />}</div>
        <div className="poll-opts">
          <div className="poll-opt">{c.poll?.opcion_1 || <FieldMissing label="opcion_1" />}</div>
          <div className="poll-opt">{c.poll?.opcion_2 || <FieldMissing label="opcion_2" />}</div>
        </div>
      </div>
      <PreviewCard label="Texto inferior / CTA" color={color}>
        <div style={{ fontSize: 12, color }}>{c.texto_inferior || <FieldMissing label="texto_inferior" />}</div>
      </PreviewCard>
      <PreviewCard label="Diseño sugerido" color={color}>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>{c.idea_diseno || <FieldMissing label="idea_diseno" />}</div>
      </PreviewCard>
    </div>
  );

  if (item.tipo === 'historia' && isHistoria(c)) return (
    <div className="preview-grid">
      <PreviewCard label="Historia de Instagram" color={color}><div className="preview-card-main">{c.texto}</div></PreviewCard>
      <div className="poll-card">
        <div className="poll-q">📊 {c.poll.pregunta}</div>
        <div className="poll-opts">
          <div className="poll-opt">{c.poll.opcion_a}</div>
          <div className="poll-opt">{c.poll.opcion_b}</div>
        </div>
      </div>
    </div>
  );

  if (item.tipo === 'reel' && isReel(c)) return (
    <div className="preview-grid">
      <PreviewCard label="Gancho" color={color}><div className="preview-card-main">{c.gancho}</div></PreviewCard>
      <PreviewCard label="Desarrollo" color={color}><div className="preview-card-main" style={{ whiteSpace: 'pre-wrap' }}>{c.desarrollo}</div></PreviewCard>
      <PreviewCard label="CTA" color={color}><div className="preview-card-main">{c.cta}</div><div className="reel-duration-badge">⏱ {c.duracion_estimada}</div></PreviewCard>
    </div>
  );

  if (item.tipo === 'pdf' && isPdf(c)) return (
    <div className="preview-grid">
      <div className="pdf-intro-card">{c.intro}</div>
      {c.secciones.map((sec, i) => (
        <div key={i} className="pdf-section-card"><div className="pdf-section-t">{i + 1}. {sec.titulo}</div><div className="pdf-section-c">{sec.contenido}</div></div>
      ))}
      <div className="pdf-conclusion-card">{c.conclusion}</div>
    </div>
  );

  if (isNuevoPdf(c)) return (
    <div className="preview-grid">
      <PreviewCard label="Portada" color={color}>
        <div className="preview-card-main">{c.portada.titulo_emocional}</div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>{c.portada.subtitulo}</div>
      </PreviewCard>
      <PreviewCard label={c.seccion_psicoeducativa.titulo || 'Psicoeducación'} color={color}>
        <div className="preview-card-main" style={{ fontSize: 13, marginBottom: 6 }}>{c.seccion_psicoeducativa.que_es}</div>
        {c.seccion_psicoeducativa.por_que_pasa && <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 6 }}>{c.seccion_psicoeducativa.por_que_pasa}</div>}
        {c.seccion_psicoeducativa.como_te_afecta && <div style={{ fontSize: 13, color: 'var(--muted)' }}>{c.seccion_psicoeducativa.como_te_afecta}</div>}
      </PreviewCard>
      <PreviewCard label="Ejercicio" color={color}>
        <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600, marginBottom: 6 }}>{(c.ejercicio as Record<string, string>).titulo}</div>
        {((c.ejercicio as Record<string, unknown>).pasos as Array<{ numero: number; titulo: string; descripcion: string; por_que_funciona?: string; tip_extra?: string }> ?? []).map(p => (
          <div key={p.numero} style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
            <div><strong style={{ color: 'var(--text)' }}>{p.numero}. {p.titulo}</strong> — {p.descripcion}</div>
            {p.por_que_funciona && <div style={{ marginTop: 2, fontStyle: 'italic' }}>💡 {p.por_que_funciona}</div>}
            {p.tip_extra && <div style={{ marginTop: 2 }}>🔧 {p.tip_extra}</div>}
          </div>
        ))}
      </PreviewCard>
      {c.material_sugerido && (
        <PreviewCard label={`Material sugerido · ${c.material_sugerido.tipo}`} color={color}>
          <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600, marginBottom: 4 }}>{c.material_sugerido.titulo}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>{c.material_sugerido.descripcion}</div>
          {(c.material_sugerido.contenido_sugerido ?? []).map((item, i) => (
            <div key={i} style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 2 }}>• {item}</div>
          ))}
        </PreviewCard>
      )}
      <PreviewCard label="CTA" color={color}>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>{c.cta.instruccion}</div>
        <div style={{ fontSize: 11, color: color, marginTop: 4, fontWeight: 600 }}>{c.cta.qr_url}</div>
      </PreviewCard>
    </div>
  );

  if (isNuevoPost(c)) {
    const desc = c.descripcion as Record<string, unknown>;
    return (
      <div className="preview-grid">
        <PreviewCard label="Estructura visual" color={color}>
          <div className="preview-card-main" style={{ fontWeight: 700 }}>{c.estructura_visual.linea_1}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>{c.estructura_visual.linea_2}</div>
          <div style={{ fontSize: 12, color: color, marginTop: 8, fontStyle: 'italic' }}>{c.estructura_visual.linea_cta}</div>
        </PreviewCard>
        <PreviewCard label="Caption" color={color}>
          <div style={{ fontSize: 12, color: 'var(--text)', marginBottom: 4 }}>🎣 {String(desc.gancho ?? '')}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>{String(desc.desarrollo ?? '')}</div>
          <div style={{ fontSize: 12, color: color, fontStyle: 'italic' }}>{String(desc.cta ?? '')}</div>
        </PreviewCard>
        <PreviewCard label="Diseño sugerido" color={color}><div style={{ fontSize: 12, color: 'var(--muted)' }}>{c.idea_diseno}</div></PreviewCard>
      </div>
    );
  }

  if (isNuevoCarrusel(c)) {
    const desc = c.descripcion as Record<string, unknown>;
    return (
      <div className="preview-grid">
        <div className="preview-card-label" style={{ paddingLeft: 0 }}>Carrusel — {c.slides.length} slides</div>
        <div className="slides-grid">
          {c.slides.map(slide => (
            <div key={slide.numero} className="slide-card">
              <div className="slide-num">Slide {slide.numero}/{slide.total}</div>
              {slide.formula && <span className="reel-formula-badge" style={{ marginBottom: 4 }}>{REEL_FORMULA_LABEL[slide.formula] ?? slide.formula}</span>}
              <div className="slide-t">{slide.titulo}</div>
              {slide.cuerpo && <div className="slide-b">{slide.cuerpo}</div>}
              {slide.idea_diseno && <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>🎨 {slide.idea_diseno}</div>}
            </div>
          ))}
        </div>
        <PreviewCard label="Caption" color={color}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{String(desc.gancho ?? '')}</div>
          <div style={{ fontSize: 12, color: color, marginTop: 4, fontStyle: 'italic' }}>{String(desc.cta ?? '')}</div>
        </PreviewCard>
      </div>
    );
  }

  if (isReelV2(c)) {
    const g = c.guion;
    const desc = c.descripcion as Record<string, unknown>;

    const renderSegmento = (key: string, label: string, tiempo: string, seg: ReelSegmento, badge?: string) => (
      <PreviewCard key={key} label={`${label} · ${tiempo}`} color={color}>
        {badge && <span className="reel-formula-badge">{badge}</span>}
        <div className="preview-card-main" style={{ fontSize: 13, marginTop: badge ? 6 : 0 }}>{seg.se_dice}</div>
        {seg.texto_pantalla && <div className="reel-screen-text">{seg.texto_pantalla}</div>}
        {seg.instruccion_visual && <div className="reel-visual-note">🎬 {seg.instruccion_visual}</div>}
      </PreviewCard>
    );

    return (
      <div className="preview-grid">
        <PreviewCard label={`Formato: ${c.formato_elegido}`} color={color}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{c.razon_formato}</div>
        </PreviewCard>

        {renderSegmento('gancho', 'Gancho', g.gancho.tiempo, g.gancho, REEL_FORMULA_LABEL[g.gancho.formula] ?? g.gancho.formula)}
        {renderSegmento('loop', 'Loop / promesa', g.loop_promesa.tiempo, g.loop_promesa)}
        {g.desarrollo.segmentos.map((seg, i) => renderSegmento(`desarrollo-${i}`, `Desarrollo ${i + 1}`, g.desarrollo.tiempo, seg))}
        {g.solucion.segmentos.map((seg, i) => renderSegmento(`solucion-${i}`, `Solución ${i + 1}`, g.solucion.tiempo, seg))}
        {renderSegmento('cta', 'CTA', g.cta.tiempo, g.cta, REEL_VARIANTE_LABEL[g.cta.variante] ?? g.cta.variante)}
        {g.cierre_loop?.se_dice && (
          <PreviewCard label={`Cierre loop · ${g.cierre_loop.tiempo}`} color={color}>
            <div className="preview-card-main" style={{ fontSize: 13 }}>{g.cierre_loop.se_dice}</div>
          </PreviewCard>
        )}

        <PreviewCard label="Edición" color={color}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Ritmo gancho/desarrollo: {c.edicion.ritmo_gancho_desarrollo}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Ritmo parte emocional: {c.edicion.ritmo_parte_emocional}</div>
          {c.edicion.ideas_edicion.length > 0 && (
            <div style={{ marginTop: 6 }}>
              {c.edicion.ideas_edicion.map((idea, i) => <div key={i} style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 2 }}>• {idea}</div>)}
            </div>
          )}
          <div style={{ fontSize: 12, color, marginTop: 6, fontStyle: 'italic' }}>🎵 {c.edicion.musica_sugerida}</div>
        </PreviewCard>

        <div className="reel-duration-badge">⏱ ~{c.validacion_duracion.duracion_estimada_segundos}s estimados · {c.validacion_duracion.palabras_totales_habladas} palabras habladas</div>

        <PreviewCard label="Caption" color={color}>
          <div style={{ fontSize: 12, color, fontStyle: 'italic' }}>{String(desc.cta ?? '')}</div>
        </PreviewCard>
      </div>
    );
  }

  if (isNuevoReel(c)) {
    const g = c.guion as Record<string, { tiempo: string; texto: string; instruccion_grabacion: string }>;
    const desc = c.descripcion as Record<string, unknown>;
    return (
      <div className="preview-grid">
        <PreviewCard label={`Formato: ${c.formato_elegido}`} color={color}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{c.razon_formato}</div>
        </PreviewCard>
        {(['gancho', 'desarrollo', 'solucion', 'cta'] as const).map(k => g[k] ? (
          <PreviewCard key={k} label={`${k.toUpperCase()} · ${g[k].tiempo}`} color={color}>
            <div className="preview-card-main" style={{ fontSize: 13 }}>{g[k].texto}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>🎬 {g[k].instruccion_grabacion}</div>
          </PreviewCard>
        ) : null)}
        {c.ideas_edicion.length > 0 && (
          <PreviewCard label="Ideas de edición" color={color}>
            {c.ideas_edicion.map((idea, i) => <div key={i} style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 2 }}>• {idea}</div>)}
          </PreviewCard>
        )}
        <PreviewCard label="Caption" color={color}>
          <div style={{ fontSize: 12, color: color, fontStyle: 'italic' }}>{String(desc.cta ?? '')}</div>
        </PreviewCard>
      </div>
    );
  }

  if (isNuevoHistoriaCta(c)) return (
    <div className="preview-grid">
      <PreviewCard label="Historia CTA" color={color}>
        <div className="preview-card-main" style={{ fontWeight: 700 }}>{c.frase_valor}</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>{c.instruccion}</div>
        <div style={{ fontSize: 16, fontWeight: 800, color, marginTop: 8 }}>{c.palabra_clave_destacada}</div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{c.texto_inferior}</div>
      </PreviewCard>
      <PreviewCard label="Diseño sugerido" color={color}>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>{c.idea_diseno}</div>
      </PreviewCard>
    </div>
  );

  return (
    <div className="preview-grid">
      <PreviewCard label="Contenido (raw)" color={color}>
        <pre style={{ fontSize: 11, color: 'var(--muted)', overflow: 'auto', maxHeight: 200 }}>{JSON.stringify(c, null, 2)}</pre>
      </PreviewCard>
    </div>
  );
}

// ─── PDF downloader ───────────────────────────────────────────────────────
function handleDescargarPdf(item: ContenidoItem) {
  const c = isPdf(item.cuerpo) ? (item.cuerpo as CuerpoPdf) : null;
  const secciones = c?.secciones ?? [];
  const seccionesHtml = secciones.map((s, i) => `
    <div class="seccion">
      <h2>${i + 1}. ${s.titulo}</h2>
      <p>${s.contenido.replace(/\n/g, '<br>')}</p>
    </div>`).join('');
  const html = `<!DOCTYPE html>
<html lang="es"><head><meta charset="utf-8">
<title>${item.titulo}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;700&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  @page { size: A4; margin: 20mm 18mm; }
  body { font-family: Inter, sans-serif; background: #FAFAF8; color: #2D2D2D; font-size: 14px; line-height: 1.8; }
  .header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 12px; border-bottom: 2px solid #B8D4C8; margin-bottom: 28px; }
  .header-brand { font-size: 18px; font-weight: 700; color: #C49A8A; }
  .title { font-size: 26px; font-weight: 700; color: #2D2D2D; margin-bottom: 10px; }
  .intro { font-size: 15px; color: #6A6A7A; margin-bottom: 28px; line-height: 1.7; }
  .seccion { margin-bottom: 24px; padding-left: 18px; border-left: 4px solid #C49A8A; }
  .seccion h2 { font-size: 15px; font-weight: 700; color: #2D2D2D; margin-bottom: 8px; }
  .seccion p { color: #4A4A5A; line-height: 1.8; }
  .conclusion { margin-top: 28px; padding: 16px 20px; background: #F0EAE4; border-radius: 8px; color: #5A4A40; font-style: italic; }
  .footer { margin-top: 36px; padding-top: 12px; border-top: 1px solid #B8D4C8; text-align: center; font-size: 12px; color: #B8D4C8; }
</style></head>
<body>
  <div class="header"><div class="header-brand">PsicoDosis</div></div>
  <h1 class="title">${item.titulo}</h1>
  ${c?.intro ? `<p class="intro">${c.intro}</p>` : ''}
  ${seccionesHtml}
  ${c?.conclusion ? `<div class="conclusion">${c.conclusion}</div>` : ''}
  <div class="footer">pzicodosis.com</div>
  <script>window.onload = () => { window.print(); }</script>
</body></html>`;
  const w = window.open('', '_blank', 'width=800,height=1000');
  if (w) { w.document.write(html); w.document.close(); }
}

// ─── Design tokens ────────────────────────────────────────────────────────
const TIPO_TONE: Record<string, { soft: string; ink: string; solid: string; line: string }> = {
  texto:        { soft: 'var(--copper-soft)', ink: 'var(--copper-ink)', solid: 'var(--copper)', line: 'var(--copper-line)' },
  carrusel:     { soft: 'var(--sage-soft)',   ink: 'var(--sage-ink)',   solid: 'var(--sage)',   line: 'var(--sage-line)'   },
  reel:         { soft: 'var(--clay-soft)',   ink: 'var(--clay)',       solid: 'var(--clay)',   line: '#e7c9c1'            },
  historia:     { soft: 'var(--amber-soft)',  ink: 'var(--amber)',      solid: 'var(--amber)',  line: '#e7d7ab'            },
  historia_cta: { soft: 'var(--amber-soft)',  ink: 'var(--amber)',      solid: 'var(--amber)',  line: '#e7d7ab'            },
  pdf:          { soft: 'var(--info-soft)',   ink: 'var(--info)',       solid: 'var(--info)',   line: '#cbd9e2'            },
};

const STATUS_DISPLAY: Record<string, { label: string; soft: string; ink: string; solid: string; line: string }> = {
  borrador:  { label: 'Pendiente', soft: 'var(--amber-soft)', ink: 'var(--amber)',    solid: 'var(--amber)', line: '#e7d7ab'          },
  aprobado:  { label: 'Aprobado',  soft: 'var(--sage-soft)',  ink: 'var(--sage-ink)', solid: 'var(--sage)',  line: 'var(--sage-line)' },
  publicado: { label: 'Publicado', soft: 'var(--info-soft)',  ink: 'var(--info)',     solid: 'var(--info)',  line: '#cbd9e2'          },
  rechazado: { label: 'Rechazado', soft: 'var(--clay-soft)',  ink: 'var(--clay)',     solid: 'var(--clay)',  line: '#e7c9c1'          },
};

const TIPO_LABEL: Record<string, string> = {
  texto: 'Post', carrusel: 'Carrusel', historia: 'Historia',
  historia_cta: 'Historia CTA', reel: 'Reel', pdf: 'PDF',
};

function relDate(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'hoy';
  if (days === 1) return 'ayer';
  if (days < 7) return `hace ${days} días`;
  if (days < 30) return `hace ${Math.floor(days / 7)} sem`;
  return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
}

function TipoTag({ tipo, small }: { tipo: string; small?: boolean }) {
  const t = TIPO_TONE[tipo] ?? TIPO_TONE.texto;
  return (
    <span className="tag" style={{ background: t.soft, color: t.ink, borderColor: t.line, height: small ? 21 : 23 }}>
      <span className="dot" style={{ background: t.solid }} />
      {TIPO_LABEL[tipo] ?? tipo}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_DISPLAY[status] ?? STATUS_DISPLAY.borrador;
  return (
    <span className="tag" style={{ background: s.soft, color: s.ink, borderColor: s.line, height: 23 }}>
      <span className="dot" style={{ background: s.solid }} />
      {s.label}
    </span>
  );
}

// ─── Tipo icons (format dots) ─────────────────────────────────────────────
const FORMAT_ICONS: Array<{ key: string; Icon: (p: { size?: number }) => JSX.Element }> = [
  { key: 'texto',        Icon: IgIco    },
  { key: 'carrusel',     Icon: GridIco  },
  { key: 'reel',         Icon: VideoIco },
  { key: 'historia',     Icon: PollIco  },
  { key: 'historia_cta', Icon: PollIco  },
  { key: 'pdf',          Icon: PdfIco   },
];

function PieceDot({ tipo }: { tipo: string }) {
  const entry = FORMAT_ICONS.find(f => f.key === tipo);
  if (!entry) return null;
  const { Icon } = entry;
  const t = TIPO_TONE[tipo] ?? TIPO_TONE.texto;
  return (
    <span title={TIPO_LABEL[tipo] ?? tipo} style={{ width: 26, height: 26, borderRadius: 7, display: 'grid', placeItems: 'center', background: t.soft, color: t.solid }}>
      <Icon size={14} />
    </span>
  );
}

// ─── Content Drawer ───────────────────────────────────────────────────────
function ContentDrawer({ item, onClose, onStatus, updating }: {
  item: ContenidoItem;
  onClose: () => void;
  onStatus: (id: string, s: string) => void;
  updating: boolean;
}) {
  const s = STATUS_DISPLAY[item.status] ?? STATUS_DISPLAY.borrador;

  return (
    <div className="scrim" onClick={onClose} style={{ background: 'rgba(47,37,25,.38)', backdropFilter: 'blur(2px)' }}>
      <div className="drawer" onClick={e => e.stopPropagation()}>
        <div className="drawer-head">
          <span className="stat-ico" style={{ background: 'var(--accent-soft)', color: 'var(--accent)', width: 42, height: 42, flex: '0 0 42px' }}>
            <LayersIco size={20} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="row" style={{ gap: 8, marginBottom: 7, flexWrap: 'wrap' }}>
              <TipoTag tipo={item.tipo} small />
              <StatusBadge status={item.status} />
            </div>
            <h2 style={{ margin: 0, fontSize: 21, lineHeight: 1.25 }}>{item.titulo}</h2>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 6 }}>
              {relDate(item.created_at)} · {item.tipo === 'pdf' ? 'Guía PDF' : TIPO_LABEL[item.tipo] ?? item.tipo}
            </div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XIco size={18} /></button>
        </div>

        {/* Tab row */}
        <div className="row" style={{ gap: 0, padding: '0 24px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
          <span className="row" style={{ gap: 8, height: 44, alignItems: 'center' }}>
            <PieceDot tipo={item.tipo} />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>{TIPO_LABEL[item.tipo] ?? item.tipo}</span>
          </span>
        </div>

        <div className="drawer-body" style={{ flex: 1 }}>
          {item.tipo === 'pdf' && (
            <div style={{ marginBottom: 14 }}>
              <button className="btn btn-sm btn-ghost" onClick={() => handleDescargarPdf(item)}>
                <DownloadIco size={14} /> Descargar PDF
              </button>
            </div>
          )}
          <Preview item={item} />
        </div>

        {/* Footer actions */}
        <div className="drawer-foot">
          {item.status === 'borrador' && (
            <>
              <span className="muted" style={{ fontSize: 12.5, marginRight: 'auto' }}>Revisa el contenido antes de aprobar</span>
              <button className="btn btn-sm btn-clay" onClick={() => onStatus(item.id, 'rechazado')} disabled={updating}>
                <XIco size={14} /> Rechazar
              </button>
              <button className="btn btn-sm btn-sage" onClick={() => onStatus(item.id, 'aprobado')} disabled={updating}>
                {updating ? <span className="spinner" /> : <CheckIco size={14} />} Aprobar
              </button>
            </>
          )}
          {item.status === 'aprobado' && (
            <>
              <span className="row" style={{ gap: 7, fontSize: 12.5, color: s.ink, marginRight: 'auto', fontWeight: 600 }}>
                <CheckIco size={14} /> Aprobado · listo para publicar
              </span>
              <button className="btn btn-sm btn-ghost" onClick={() => onStatus(item.id, 'rechazado')} disabled={updating}>
                <XIco size={14} /> Rechazar
              </button>
              <button className="btn btn-sm btn-primary" onClick={() => onStatus(item.id, 'publicado')} disabled={updating}>
                {updating ? <span className="spinner" style={{ borderColor: 'rgba(255,255,255,.3)', borderTopColor: '#fff' }} /> : <IgIco size={14} />}
                {updating ? 'Actualizando…' : 'Marcar publicado'}
              </button>
            </>
          )}
          {item.status === 'publicado' && (
            <span className="row" style={{ gap: 7, fontSize: 13, color: 'var(--ink-2)', fontWeight: 600 }}>
              <IgIco size={15} /> Publicado
              {item.published_at && ` · ${new Date(item.published_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Content Card ─────────────────────────────────────────────────────────
function ContentCard({ item, onOpen, onStatus, updating }: {
  item: ContenidoItem;
  onOpen: (i: ContenidoItem) => void;
  onStatus: (id: string, s: string) => void;
  updating: boolean;
}) {
  const snippet = getSnippet(item.cuerpo);

  return (
    <div className="card fade-up"
      style={{ display: 'flex', flexDirection: 'column', cursor: 'pointer', transition: 'border-color .15s, box-shadow .15s, transform .08s' }}
      onClick={() => onOpen(item)}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'var(--shadow-xs)'; e.currentTarget.style.transform = 'none'; }}>
      <div className="card-pad" style={{ paddingBottom: 14 }}>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
          <TipoTag tipo={item.tipo} small />
          <StatusBadge status={item.status} />
        </div>
        <div style={{ fontFamily: 'var(--serif)', fontSize: 18.5, lineHeight: 1.28, letterSpacing: '-0.01em', minHeight: 72 }}>
          {item.titulo}
        </div>
        {snippet && (
          <div className="muted" style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {snippet}{snippet.length === 80 ? '…' : ''}
          </div>
        )}
        <div className="row" style={{ justifyContent: 'space-between', marginTop: 12 }}>
          <PieceDot tipo={item.tipo} />
          <span className="muted" style={{ fontSize: 12 }}>{relDate(item.created_at)}</span>
        </div>
      </div>
      <hr className="hr" />
      <div className="row card-pad" style={{ paddingTop: 12, paddingBottom: 12, gap: 8 }}
        onClick={e => e.stopPropagation()}>
        {item.status === 'borrador' && (
          <>
            <button className="btn btn-sm btn-sage" style={{ flex: 1 }}
              onClick={() => onStatus(item.id, 'aprobado')} disabled={updating}>
              {updating ? <span className="spinner" /> : <CheckIco size={13} />} Aprobar
            </button>
            <button className="btn btn-sm btn-clay" style={{ flex: 1 }}
              onClick={() => onStatus(item.id, 'rechazado')} disabled={updating}>
              <XIco size={13} /> Rechazar
            </button>
          </>
        )}
        {item.status === 'aprobado' && (
          <button className="btn btn-sm btn-primary" style={{ flex: 1 }}
            onClick={() => onStatus(item.id, 'publicado')} disabled={updating}>
            {updating ? <span className="spinner" style={{ borderColor: 'rgba(255,255,255,.3)', borderTopColor: '#fff' }} /> : <IgIco size={13} />}
            {updating ? 'Actualizando…' : 'Marcar publicado'}
          </button>
        )}
        {item.status === 'publicado' && (
          <span className="row" style={{ gap: 6, fontSize: 12.5, color: 'var(--ink-2)', fontWeight: 600, flex: 1 }}>
            <IgIco size={14} /> Publicado
          </span>
        )}
        <button className="btn btn-sm btn-ghost" onClick={() => onOpen(item)}>
          <EyeIco size={13} /> Ver
        </button>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────
// "Todos" muestra solo borrador+aprobado (trabajo activo). Rechazadas se
// excluyen siempre (el backend ya las omite por defecto); publicadas viven
// en el módulo Historial, que tiene fechas y diseño dedicados a eso.
export default function Generacion() {
  const navigate = useNavigate();
  const [items, setItems]         = useState<ContenidoItem[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [statusFilter, setStatus] = useState('');
  const [tipoFilter, setTipo]     = useState('');
  const [drawerItem, setDrawer]   = useState<ContenidoItem | null>(null);
  const [updatingId, setUpdating] = useState<string | null>(null);
  const [toast, setToast]         = useState<ToastState | null>(null);

  const showToast = useCallback((msg: string, type: ToastState['type']) => setToast({ message: msg, type }), []);

  useEffect(() => {
    setLoading(true);
    generacionApi.getContenido({})
      .then(r => setItems(r.items.filter(i => i.status !== 'publicado')))
      .catch(e => setError(e instanceof Error ? e.message : 'Error'))
      .finally(() => setLoading(false));
  }, []);

  const handleStatus = async (id: string, newStatus: string) => {
    setUpdating(id);
    try {
      await generacionApi.updateStatus(id, newStatus);
      if (newStatus === 'rechazado' || newStatus === 'publicado') {
        // Ya no pertenecen a la vista de trabajo activo de Generación
        setItems(prev => prev.filter(i => i.id !== id));
        setDrawer(prev => prev?.id === id ? null : prev);
      } else {
        setItems(prev => prev.map(i => i.id === id ? { ...i, status: newStatus as ContenidoItem['status'] } : i));
        setDrawer(prev => prev?.id === id ? { ...prev, status: newStatus as ContenidoItem['status'] } : prev);
      }
      if (newStatus === 'publicado') showToast('Marcado como publicado', 'success');
      else if (newStatus === 'aprobado') showToast('Contenido aprobado', 'success');
      else if (newStatus === 'rechazado') showToast('Contenido rechazado', 'success');
      else showToast('Devuelto a pendiente', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error al actualizar', 'error');
    } finally {
      setUpdating(null);
    }
  };

  const counts: Record<string, number> = {
    '': items.length,
    borrador:  items.filter(i => i.status === 'borrador').length,
    aprobado:  items.filter(i => i.status === 'aprobado').length,
  };

  const tipos = [...new Set(items.map(i => i.tipo))];

  const filtered = items.filter(i => {
    if (statusFilter && i.status !== statusFilter) return false;
    if (tipoFilter && i.tipo !== tipoFilter) return false;
    return true;
  });

  const STATUS_TABS = [
    { val: '', label: 'Todos' },
    { val: 'borrador',  label: 'Pendientes' },
    { val: 'aprobado',  label: 'Aprobados'  },
  ];

  return (
    <div className="page page-wide">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 28, margin: 0 }}>Generación</h1>
          <p className="muted" style={{ margin: '7px 0 0', fontSize: 14, maxWidth: 560 }}>
            Todo el contenido creado por IA. Revisa cada pieza, apruébala o recházala, y márcala como publicada.
          </p>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Status seg */}
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div className="seg">
          {STATUS_TABS.map(t => (
            <button key={t.val} className={statusFilter === t.val ? 'on' : ''}
              onClick={() => setStatus(t.val)}>
              {t.label}{counts[t.val] > 0 && <span style={{ opacity: .6 }}> · {counts[t.val]}</span>}
            </button>
          ))}
        </div>
        <button className="btn btn-sm btn-ghost" onClick={() => navigate('/historial')}>
          <IgIco size={13} /> Ver publicados en Historial
        </button>
      </div>

      {/* Tipo chips */}
      {tipos.length > 1 && (
        <div className="row" style={{ gap: 7, flexWrap: 'wrap', marginBottom: 20 }}>
          <button onClick={() => setTipo('')} className="tag"
            style={{ cursor: 'pointer', height: 30, fontWeight: 600,
              border: tipoFilter === '' ? '1px solid var(--accent)' : '1px solid var(--border)',
              background: tipoFilter === '' ? 'var(--accent-soft)' : 'var(--surface-2)',
              color: tipoFilter === '' ? 'var(--accent-ink)' : 'var(--ink-2)' }}>
            Todos
          </button>
          {tipos.map(tip => (
            <button key={tip} onClick={() => setTipo(tip === tipoFilter ? '' : tip)} className="tag"
              style={{ cursor: 'pointer', height: 30, fontWeight: 600,
                border: tipoFilter === tip ? '1px solid var(--accent)' : '1px solid var(--border)',
                background: tipoFilter === tip ? 'var(--accent-soft)' : 'var(--surface-2)',
                color: tipoFilter === tip ? 'var(--accent-ink)' : 'var(--ink-2)' }}>
              {TIPO_LABEL[tip] ?? tip}
            </button>
          ))}
        </div>
      )}

      {loading && <div className="loading-center"><span className="spinner" /> Cargando contenido…</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ padding: '60px 20px', textAlign: 'center' }}>
          <span style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--surface-2)', display: 'grid', placeItems: 'center', margin: '0 auto 14px', color: 'var(--ink-2)' }}>
            <LayersIco size={24} />
          </span>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 17, color: 'var(--ink)' }}>Sin contenido</div>
          <div style={{ fontSize: 13, marginTop: 4, color: 'var(--muted)' }}>
            {statusFilter || tipoFilter ? 'Prueba cambiando los filtros' : 'Ve a Inteligencia y genera contenido de un tema'}
          </div>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(316px, 1fr))' }}>
          {filtered.map(item => (
            <ContentCard key={item.id} item={item}
              onOpen={setDrawer} onStatus={handleStatus} updating={updatingId === item.id} />
          ))}
        </div>
      )}

      {drawerItem && (
        <ContentDrawer item={drawerItem} onClose={() => setDrawer(null)}
          onStatus={handleStatus} updating={updatingId === drawerItem.id} />
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

/* ── Icons ── */
function Svg({ children, size = 18 }: { children: React.ReactNode; size?: number }) {
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;
}
function LayersIco({ size = 18 }: { size?: number })   { return <Svg size={size}><path d="M2 12l10 5 10-5M2 17l10 5 10-5M12 2 2 7l10 5 10-5z"/></Svg>; }
function CheckIco({ size = 18 }: { size?: number })    { return <Svg size={size}><path d="M5 12.5l4.5 4.5L19 6.5"/></Svg>; }
function XIco({ size = 18 }: { size?: number })        { return <Svg size={size}><path d="M6 6l12 12M18 6 6 18"/></Svg>; }
function EyeIco({ size = 18 }: { size?: number })      { return <Svg size={size}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></Svg>; }
function IgIco({ size = 18 }: { size?: number })       { return <Svg size={size}><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r=".5" fill="currentColor" stroke="none"/></Svg>; }
function GridIco({ size = 18 }: { size?: number })     { return <Svg size={size}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></Svg>; }
function VideoIco({ size = 18 }: { size?: number })    { return <Svg size={size}><path d="M15 10l5-3v10l-5-3V8a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-6z"/></Svg>; }
function PollIco({ size = 18 }: { size?: number })     { return <Svg size={size}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 12h8M8 8h4M8 16h6"/></Svg>; }
function PdfIco({ size = 18 }: { size?: number })      { return <Svg size={size}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/></Svg>; }
function DownloadIco({ size = 18 }: { size?: number }) { return <Svg size={size}><path d="M12 4v12m-5-5 5 5 5-5"/><path d="M4 20h16"/></Svg>; }
